import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout, continueSessionTimeoutAsyn, setSessionTempTime, getSessionTimeOutAsyn } from "./app/userSlice";
import { Modal } from "./components/secondary";
import style from "./components/core/Menu/menu.module.scss";
import continueSignout from "./static/images/continue-signout.png";

export const RenderSession = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const sessionTimeOutData = useSelector((state) => state.user.sessionTimeOutData);

  const [timer, setTimer] = useState(null);
  const [isActive, setIsActive] = useState(true);

  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const autoExtendRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const userWasActiveRef = useRef(false);

  const convertToMilliseconds = (timeout) => {
    return timeout * 60 * 1000;
  };

  const handleLogout = () => {
    clearTimeout(timeoutRef.current);
    clearTimeout(autoExtendRef.current);
    clearInterval(intervalRef.current);
    setIsActive(false);
  };

  const trackActivity = () => {
    lastActivityRef.current = Date.now();
    userWasActiveRef.current = true;
  };

  const checkAndExtendSession = () => {
    const timeSinceActivity = Date.now() - lastActivityRef.current;
    const isUserActive = timeSinceActivity < 30000;

    if (isUserActive && userWasActiveRef.current) {
      dispatch(continueSessionTimeoutAsyn(user?.userDetails?.email));
      userWasActiveRef.current = false;
      initializeTimeout();
    }
  };

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const initializeTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (autoExtendRef.current) clearTimeout(autoExtendRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (!sessionTimeOutData?.sessionTimeout) return;

    const { sessionTimeout } = sessionTimeOutData;
    const timeoutMs = convertToMilliseconds(sessionTimeout);

    const MAX_TIMEOUT = 2147483647;

    if (timeoutMs > MAX_TIMEOUT) {  
      setTimer(timeoutMs);    
      timeoutRef.current = setTimeout(() => {
        initializeTimeout();
      }, timeoutMs - MAX_TIMEOUT + 60000);
      
      const endTime = Date.now() + timeoutMs;
      intervalRef.current = setInterval(() => {
        const remaining = endTime - Date.now();
        if (remaining <= 0) {
          clearInterval(intervalRef.current);
          handleLogout();
        } else {
          setTimer(remaining);
        }
      }, 60000);
      
      return;
    }

    const autoExtendMs = timeoutMs - (30 * 1000);

    setTimer(timeoutMs);

    autoExtendRef.current = setTimeout(() => {
      checkAndExtendSession();
    }, Math.max(0, autoExtendMs));

    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, timeoutMs);

    const endTime = Date.now() + timeoutMs;
    intervalRef.current = setInterval(() => {
      const remaining = endTime - Date.now();
      if (remaining <= 0) {
        setTimer(0);
        clearInterval(intervalRef.current);
      } else {
        setTimer(remaining);
      }
    }, 1000);
  };

  const onUserActivity = () => {
    if (isActive) {
      trackActivity();
    }
  };

  useEffect(() => {
    if (user?.userDetails?.email) {
      dispatch(getSessionTimeOutAsyn(user?.userDetails?.email));
    }
  }, [dispatch, user?.userDetails?.email]);

  useEffect(() => {
    if (sessionTimeOutData?.sessionTimeout) {
      initializeTimeout();

      const events = ["mousedown", "keydown", "click", "touchstart", "scroll", "mousemove"];
      events.forEach((event) => {
        document.addEventListener(event, onUserActivity);
      });

      return () => {
        clearTimeout(timeoutRef.current);
        clearTimeout(autoExtendRef.current);
        clearInterval(intervalRef.current);

        events.forEach((event) => {
          document.removeEventListener(event, onUserActivity);
        });
      };
    }
  }, [sessionTimeOutData, isActive]);

  const modalCloseSessionOut = () => {
    setIsActive(true);
    dispatch(setSessionTempTime(""));
    dispatch(logout());
  };

  return (
    <>
      <div>
        <p className={"d-none"}>{timer ? formatTime(timer) : ""}</p>
        <input type="hidden" className={"finaltime"} value={timer ? formatTime(timer) : ""} />

        {!isActive && (
          <Modal
            modalOpen={!isActive}
            modalClose={modalCloseSessionOut}
            heading={"Session Timed Out"}
            className={"modal_box"}
            modalSize={style.custom_modal_size}
            custom_heading={style.custom_heading}
            custom_body={style.custom_body}
            custom_content={style.custom_content}
          >
            <div className="col-sm-12 text-center">
              <div className={`pt-2 pb-2 ${style.cont_sign_main} text-center`}>
                <p className={style.con_sign_content}>
                  {" "}
                  <img src={continueSignout} alt="icon" className={style.con_sign_img} />
                  <b className="ml-3">Your session expired</b>
                </p>
                <p className={`${style.con_sign_time} text-center`}>
                  You've been gone for <br />a while
                </p>
                <p className={`${style.con_sign_content} text-center`}>
                  <b>
                    We closed down what you were working on <br />
                    to keep your information safe.
                  </b>
                </p>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </>
  );
};
